/**
 * Edit Section Dialog Component
 * Modal for editing an existing BOQ section — Studio theme
 */

import { Loader2, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FieldInput, FloatPanel, StudioButton } from "@/features/studio/theme";
import {
	type BOQSection,
	deleteBOQSection,
	updateBOQSection,
} from "../../services/boq/boq-api.service";

interface EditSectionDialogProps {
	boqId: string;
	section: BOQSection | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

// ── Shared styled label ────────────────────────────────────────────────────────
function FieldLabel({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) {
	return (
		<label
			htmlFor={htmlFor}
			style={{
				display: "block",
				fontSize: 11,
				fontFamily: "var(--font-mono)",
				fontWeight: 600,
				letterSpacing: "0.09em",
				textTransform: "uppercase",
				color: "var(--color-dashboard-muted)",
				marginBottom: 6,
			}}
		>
			{children}
		</label>
	);
}

// ── Shared styled textarea ─────────────────────────────────────────────────────
function FieldTextarea({
	id,
	placeholder,
	value,
	onChange,
	rows = 3,
}: {
	id?: string;
	placeholder?: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	rows?: number;
}) {
	return (
		<textarea
			id={id}
			placeholder={placeholder}
			value={value}
			onChange={onChange}
			rows={rows}
			style={{
				width: "100%",
				padding: "10px 14px",
				fontSize: 14,
				fontFamily: "var(--font-body)",
				color: "var(--color-dashboard-text)",
				background: "var(--color-dashboard-paper)",
				border: "1px solid var(--color-dashboard-border)",
				borderRadius: 7,
				outline: "none",
				resize: "vertical",
				transition: "border-color 200ms, box-shadow 200ms",
				lineHeight: 1.5,
			}}
			onFocus={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-accent)";
				e.currentTarget.style.boxShadow =
					"0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)";
			}}
			onBlur={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-border)";
				e.currentTarget.style.boxShadow = "none";
			}}
		/>
	);
}

export function EditSectionDialog({
	boqId,
	section,
	open,
	onOpenChange,
	onSuccess,
}: EditSectionDialogProps) {
	const [name, setName] = useState("");
	const [category, setCategory] = useState("");
	const [description, setDescription] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	// Reset form when section changes or dialog opens
	useEffect(() => {
		if (section && open) {
			setName(section.sectionName);
			setCategory(section.category || "");
			setDescription(section.description || "");
		}
	}, [section, open]);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		const handle = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				if (showDeleteConfirm) {
					setShowDeleteConfirm(false);
				} else {
					onOpenChange(false);
				}
			}
		};
		window.addEventListener("keydown", handle);
		return () => window.removeEventListener("keydown", handle);
	}, [open, onOpenChange, showDeleteConfirm]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!section || !name.trim()) return;

		setIsSubmitting(true);
		try {
			const result = await updateBOQSection(boqId, section.id, {
				sectionName: name.trim(),
				category: category.trim() || undefined,
				description: description.trim() || undefined,
			});

			if (result.success) {
				toast.success("Section updated successfully");
				onSuccess();
				onOpenChange(false);
			} else {
				toast.error(result.error || "Failed to update section");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!section) return;

		setIsDeleting(true);
		try {
			const result = await deleteBOQSection(boqId, section.id);

			if (result.success) {
				toast.success("Section deleted successfully");
				setShowDeleteConfirm(false);
				onOpenChange(false);
				onSuccess();
			} else {
				toast.error(result.error || "Failed to delete section");
			}
		} finally {
			setIsDeleting(false);
		}
	};

	if (!open || !section) return null;

	return (
		<>
			{/* Edit Section Modal */}
			<div
				role="dialog"
				aria-modal="true"
				style={{
					position: "fixed",
					inset: 0,
					zIndex: 200,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: "20px 16px",
					background: "rgba(30, 41, 59, 0.45)",
					backdropFilter: "blur(4px)",
				}}
				onClick={(e) => {
					if (e.target === e.currentTarget) onOpenChange(false);
				}}
				onKeyDown={(e) => {
					if (e.key === "Escape") onOpenChange(false);
				}}
			>
				<FloatPanel
					elevation={4}
					padding={0}
					style={{ width: "100%", maxWidth: 460, overflow: "hidden" }}
				>
					{/* Header */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							padding: "20px 24px 16px",
							borderBottom: "1px solid var(--color-dashboard-border)",
						}}
					>
						<div>
							<p
								style={{
									fontSize: 10,
									fontFamily: "var(--font-mono)",
									fontWeight: 600,
									letterSpacing: "0.14em",
									textTransform: "uppercase",
									color: "var(--color-dashboard-accent-dark)",
									marginBottom: 4,
								}}
							>
								BOQ
							</p>
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 20,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									letterSpacing: "-0.01em",
								}}
							>
								Edit Section
							</h2>
						</div>
						<button
							type="button"
							onClick={() => onOpenChange(false)}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 32,
								height: 32,
								borderRadius: 6,
								border: "1px solid var(--color-dashboard-border)",
								background: "transparent",
								cursor: "pointer",
								color: "var(--color-dashboard-muted)",
								transition: "background 150ms",
							}}
							onMouseEnter={(e) =>
								((e.currentTarget as HTMLElement).style.background =
									"var(--color-dashboard-divider)")
							}
							onMouseLeave={(e) =>
								((e.currentTarget as HTMLElement).style.background =
									"transparent")
							}
						>
							<X size={15} strokeWidth={2} />
						</button>
					</div>

					{/* Body */}
					<form onSubmit={handleSubmit}>
						<div
							style={{
								padding: "20px 24px",
								display: "flex",
								flexDirection: "column",
								gap: 16,
							}}
						>
							<div>
								<FieldInput
									label="Section Name *"
									id="edit-section-name"
									placeholder="e.g. Electrical Works"
									value={name}
									onChange={(e) => setName(e.target.value)}
									required
								/>
							</div>
							<div>
								<FieldInput
									label="Category (optional)"
									id="edit-section-category"
									placeholder="e.g. electrical, civil, plumbing"
									value={category}
									onChange={(e) => setCategory(e.target.value)}
								/>
							</div>
							<div>
								<FieldLabel htmlFor="edit-section-desc">
									Description (optional)
								</FieldLabel>
								<FieldTextarea
									id="edit-section-desc"
									placeholder="Provide context for this section…"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									rows={3}
								/>
							</div>

							{/* Delete button for non-default sections */}
							{!section.isDefault && (
								<button
									type="button"
									onClick={() => setShowDeleteConfirm(true)}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 8,
										width: "100%",
										height: 40,
										borderRadius: 7,
										border:
											"1px solid var(--color-dashboard-danger-border-strong)",
										background: "var(--color-dashboard-danger-bg)",
										color: "var(--color-dashboard-pill-risk)",
										fontFamily: "var(--font-body)",
										fontSize: 13.5,
										fontWeight: 500,
										cursor: "pointer",
										transition: "background 150ms",
										marginTop: 4,
									}}
									onMouseEnter={(e) =>
										((e.currentTarget as HTMLElement).style.background =
											"var(--color-dashboard-pill-risk-bg)")
									}
									onMouseLeave={(e) =>
										((e.currentTarget as HTMLElement).style.background =
											"var(--color-dashboard-danger-bg)")
									}
								>
									<Trash2 size={15} />
									Delete Section
								</button>
							)}
						</div>

						{/* Footer */}
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => onOpenChange(false)}
							>
								Cancel
							</StudioButton>
							<StudioButton
								variant="primary"
								size="sm"
								type="submit"
								disabled={isSubmitting || !name.trim()}
							>
								{isSubmitting ? "Saving…" : "Save Changes"}
							</StudioButton>
						</div>
					</form>
				</FloatPanel>
			</div>

			{/* Delete Confirmation Modal */}
			{showDeleteConfirm && (
				<div
					role="dialog"
					aria-modal="true"
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 210,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "20px 16px",
						background: "rgba(30, 41, 59, 0.45)",
						backdropFilter: "blur(4px)",
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) setShowDeleteConfirm(false);
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") setShowDeleteConfirm(false);
					}}
				>
					<FloatPanel
						elevation={4}
						padding={0}
						style={{ width: "100%", maxWidth: 420, overflow: "hidden" }}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 18,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 10px 0",
								}}
							>
								Delete Section
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								Are you sure you want to delete "{section.sectionName}"? This
								will also delete all entries in this section. This action cannot
								be undone.
							</p>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => setShowDeleteConfirm(false)}
								disabled={isDeleting}
							>
								Cancel
							</StudioButton>
							<button
								type="button"
								onClick={handleDelete}
								disabled={isDeleting}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									height: 36,
									paddingLeft: 14,
									paddingRight: 14,
									fontSize: 13,
									fontFamily: "var(--font-body)",
									fontWeight: 500,
									borderRadius: 8,
									border: "none",
									background: "var(--color-dashboard-danger-fill)",
									color: "var(--color-dashboard-on-fill)",
									cursor: isDeleting ? "not-allowed" : "pointer",
									opacity: isDeleting ? 0.6 : 1,
									transition: "background 150ms",
								}}
								onMouseEnter={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-danger)";
								}}
								onMouseLeave={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-pill-risk)";
								}}
							>
								{isDeleting ? (
									<>
										<Loader2
											size={14}
											style={{
												marginRight: 6,
												animation: "spin 1s linear infinite",
											}}
										/>
										Deleting…
									</>
								) : (
									"Delete Section"
								)}
							</button>
						</div>
					</FloatPanel>
				</div>
			)}
		</>
	);
}
